import React, { useState, useRef } from "react";
import axios from "axios";
import { Sparkles, ArrowUpRight, RotateCcw, Loader2, Star } from "lucide-react";
import { bestSellers } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    key: "hair_type",
    label: "What's your hair type?",
    options: ["Straight", "Wavy", "Curly", "Coily"],
  },
  {
    key: "concern",
    label: "What's your biggest concern?",
    options: ["Frizz & unmanageable", "Damage & breakage", "Dryness", "Dull, no shine", "Thinning / hair loss"],
  },
  {
    key: "goal",
    label: "What result do you want most?",
    options: ["Smooth & straighten", "Repair & strengthen", "Deep hydration", "Add shine", "Boost growth"],
  },
  {
    key: "chemical_history",
    label: "Any chemical treatments recently?",
    options: ["Yes, recently", "No"],
  },
  {
    key: "length",
    label: "How long is your hair?",
    options: ["Short", "Medium", "Long"],
  },
];

function getQuizSession() {
  let id = localStorage.getItem("ybera_quiz_session");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `q-${Date.now()}`;
    localStorage.setItem("ybera_quiz_session", id);
  }
  return id;
}

export default function HairQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const sessionId = useRef(getQuizSession());

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];

  const choose = async (value) => {
    const next = { ...answers, [current.key]: value };
    setAnswers(next);
    if (step < total - 1) {
      setStep(step + 1);
      return;
    }
    // last question answered -> submit
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/hair-quiz`, {
        session_id: sessionId.current,
        ...next,
      });
      setResult(data);
    } catch (e) {
      setError("Something went wrong finding your match. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setError("");
  };

  const product = result ? bestSellers.find((p) => p.id === result.product_id) || bestSellers[0] : null;

  return (
    <section className="section bg-[#1a1a1a] text-[#FBF8F4]" data-testid="hair-quiz-section">
      <div className="container-y px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="inline-flex items-center gap-2 font-sans-y text-[11px] tracking-[0.35em] uppercase text-[#C9A961] mb-4">
            <Sparkles size={14} /> AI Hair Quiz
          </p>
          <h2 className="font-serif-y text-4xl md:text-5xl leading-tight">Find your perfect Ybera match</h2>
          <p className="font-sans-y text-[14px] text-[#b5b0a4] mt-4">
            Answer 5 quick questions and our AI stylist will recommend the ideal kit for your hair.
          </p>
        </div>

        <div className="max-w-xl mx-auto bg-[#232323] border border-[#333] p-6 md:p-8">
          {!result && (
            <div data-testid="quiz-step">
              <div className="flex items-center justify-between mb-5">
                <span className="font-sans-y text-[11px] tracking-[0.2em] uppercase text-[#8a8578]">
                  Question {step + 1} of {total}
                </span>
                <div className="flex-1 ml-4 h-[3px] bg-[#333] overflow-hidden">
                  <div
                    className="h-full bg-[#C9A961] transition-all duration-300"
                    style={{ width: `${((step + (loading ? 1 : 0)) / total) * 100}%` }}
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-[#C9A961]" data-testid="quiz-loading">
                  <Loader2 size={28} className="animate-spin" />
                  <p className="font-sans-y text-[13px] text-[#b5b0a4]">Finding your perfect match...</p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif-y text-2xl mb-6">{current.label}</h3>
                  <div className="flex flex-col gap-3">
                    {current.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => choose(opt)}
                        data-testid={`quiz-option-${current.key}`}
                        className="text-left px-5 py-4 border border-[#3a3a3a] font-sans-y text-[14px] hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-colors flex items-center justify-between group"
                      >
                        {opt}
                        <ArrowUpRight size={16} className="text-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                  {error && <p className="font-sans-y text-[13px] text-red-400 mt-4" data-testid="quiz-error">{error}</p>}
                </>
              )}
            </div>
          )}

          {result && product && (
            <div data-testid="quiz-result">
              <p className="font-sans-y text-[11px] tracking-[0.3em] uppercase text-[#C9A961] mb-3">Your perfect match</p>
              <p className="font-sans-y text-[14px] text-[#e8e2d6] mb-6" data-testid="quiz-result-intro">{result.intro}</p>

              <div className="bg-[#FBF8F4] text-[#1a1a1a] p-5 flex gap-4 items-center">
                <img
                  src={product.img}
                  alt={product.name}
                  className="w-24 h-24 object-cover flex-shrink-0 bg-white"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1 text-[#C9A961] mb-1">
                    <Star size={13} fill="currentColor" />
                    <span className="font-sans-y text-[12px] text-[#555]">{product.rating?.toFixed(1)}</span>
                  </div>
                  <h4 className="font-serif-y text-[17px] leading-tight" data-testid="quiz-result-name">{product.name}</h4>
                  <p className="font-sans-y text-[14px] mt-1">
                    <span className="font-semibold">${product.price?.toFixed(2)}</span>
                    {product.compareAt && (
                      <span className="text-[#999] line-through ml-2 text-[12px]">${product.compareAt.toFixed(2)}</span>
                    )}
                  </p>
                </div>
              </div>

              <p className="font-sans-y text-[14px] leading-relaxed text-[#cfc9bd] mt-5" data-testid="quiz-result-reason">
                {result.reason}
              </p>

              <a
                href={product.href}
                target="_blank"
                rel="noreferrer"
                data-testid="quiz-shop-btn"
                className="mt-6 w-full justify-center inline-flex items-center gap-2 px-6 py-3.5 bg-[#C9A961] text-[#1a1a1a] font-sans-y text-[12px] tracking-[0.2em] uppercase font-semibold hover:bg-[#d8bd7c] transition-colors"
              >
                Shop this kit <ArrowUpRight size={16} />
              </a>

              <button
                onClick={restart}
                data-testid="quiz-restart-btn"
                className="mt-4 w-full justify-center inline-flex items-center gap-2 font-sans-y text-[12px] tracking-[0.15em] uppercase text-[#8a8578] hover:text-[#C9A961] transition-colors"
              >
                <RotateCcw size={14} /> Retake quiz
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
